import React, { useCallback, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Toaster, toast } from 'sonner';
import { PageErrorBoundary } from './components/layout/PageErrorBoundary';
import { NavSection, Sidebar } from './components/layout/Sidebar';
import { TopBar } from './components/layout/TopBar';
import { RequestTimeOffDialog } from './components/self/RequestTimeOffDialog';
import { SELF_ID } from './data/selfService';
import { useHRStore } from './hooks/useHRStore';
import { Dashboard } from './pages/Dashboard';
import { EmployeeProfile } from './pages/EmployeeProfile';
import { Employees } from './pages/Employees';
import { Holidays } from './pages/Holidays';
import { PolicyDetail } from './pages/PolicyDetail';
import { PolicyEditor } from './pages/PolicyEditor';
import { Reports } from './pages/Reports';
import { Schedule } from './pages/Schedule';
import { Settings } from './pages/Settings';
import { TimeOff } from './pages/TimeOff';
import { MyDocuments } from './pages/self/MyDocuments';
import { MyHome } from './pages/self/MyHome';
import { MyInfo } from './pages/self/MyInfo';
import { MyPay } from './pages/self/MyPay';
import { MyTimeOff } from './pages/self/MyTimeOff';
import type { Route } from './types/hr';
import type { AppView } from './types/selfService';
import { nameById } from './utils/employees';
import { formatDays } from './utils/format';
import { getHRState, hrActions } from './utils/hrStore';
interface AppProps {
  initialView?: 'employee' | 'admin';
}
export function App({
  initialView = 'employee'
}: AppProps) {
  const {
    requests
  } = useHRStore();
  const [view, setView] = useState<AppView>(initialView);
  const [route, setRoute] = useState<Route>(homeFor(initialView));
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [requestOpen, setRequestOpen] = useState(false);
  const navigate = useCallback((next: Route) => {
    setRoute(next);
    setMobileNavOpen(false);
    window.scrollTo({
      top: 0
    });
  }, []);
  const switchView = useCallback((next: AppView) => {
    if (next === view) return;
    setView(next);
    navigate(homeFor(next));
    toast(next === 'admin' ? 'Switched to Admin view' : 'Switched to My view', {
      description: next === 'admin' ? 'Signed in as Jordan Avery · HR Admin' : 'Signed in as Jordan Sample · Loan Processor'
    });
  }, [view, navigate]);
  const decideRequest = useCallback((id: string, status: 'approved' | 'denied') => {
    const request = getHRState().requests.find(r => r.id === id);
    hrActions.decideRequest(id, status);
    if (!request) return;
    const name = nameById(request.employeeId);
    if (status === 'approved') {
      toast.success(`Approved ${name}'s request`, {
        description: `${formatDays(request.days)} added to the Who's Out calendar.`
      });
    } else {
      toast(`Denied ${name}'s request`, {
        description: 'Removed from the queue. They will be notified by email.'
      });
    }
  }, []);
  const current = isAllowed(route, view) ? route : homeFor(view);
  const pendingCount = view === 'admin' ? requests.filter(r => r.status === 'pending').length : requests.filter(r => r.status === 'pending' && r.employeeId === SELF_ID).length;
  const openRequest = () => setRequestOpen(true);
  return <div className="min-h-screen w-full bg-canvas font-sans text-slate-700" data-id="element-0" data-mp="13dwshsdsypsr">
      <Sidebar view={view} active={sectionFor(current)} pendingCount={pendingCount} mobileOpen={mobileNavOpen} onClose={() => setMobileNavOpen(false)} onNavigate={section => navigate(SECTION_ROUTES[section])} data-id="element-1" data-mp="1r4io2k6mehlv" />
      <div className="lg:pl-72" data-id="element-2" data-mp="1i3azittwov4z">
        <TopBar view={view} onViewChange={switchView} onNavigate={navigate} onOpenMenu={() => setMobileNavOpen(true)} data-id="element-3" data-mp="1m4wag9mvqzcj" />
        <main className="mx-auto max-w-[1440px] px-6 py-8 lg:px-10" data-id="element-4" data-mp="6o5wtptrywld">
          <AnimatePresence mode="wait" data-id="element-5" data-mp="aq0zaxnfzg0q">
            <motion.div key={`${view}-${routeKey(current)}`} initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -4
          }} transition={{
            duration: 0.18,
            ease: 'easeOut'
          }} data-id="element-6" data-mp="ar278t95vme8">
              <PageErrorBoundary key={`${view}-${routeKey(current)}`} onReset={() => navigate(homeFor(view))} data-id="element-7" data-mp="h6kmeo9uy7xw">
                {current.page === 'dashboard' && <Dashboard requests={requests} onDecide={decideRequest} onNavigate={navigate} data-id="element-8" data-mp="c66auw91sfv4" />}
                {current.page === 'employees' && <Employees onNavigate={navigate} segment={current.segment} data-id="element-9" data-mp="14x6gfbn11row" />}
                {current.page === 'profile' && <EmployeeProfile employeeId={current.employeeId} initialTab={current.tab} requests={requests} onNavigate={navigate} data-id="element-10" data-mp="zxao49nozop6" />}
                {current.page === 'timeoff' && <TimeOff tab={current.tab} requests={requests} onDecide={decideRequest} onNavigate={navigate} data-id="element-11" data-mp="vybdq9j6yzqc" />}
                {current.page === 'policy' && <PolicyDetail policyId={current.policyId} onNavigate={navigate} data-id="element-12" data-mp="3gm7rdms1wtif" />}
                {current.page === 'policy-edit' && <PolicyEditor policyId={current.policyId} onNavigate={navigate} data-id="element-13" data-mp="1w5q5iiva4bc2" />}
                {current.page === 'reports' && <Reports data-id="element-14" data-mp="206jrh6p5vg2b" />}
                {current.page === 'settings' && <Settings onNavigate={navigate} data-id="element-15" data-mp="254lbpuybplfd" />}
                {current.page === 'holidays' && <Holidays view={view} data-id="element-16" data-mp="29vjur2ap4xmb" />}
                {current.page === 'schedule' && <Schedule view={view} data-id="element-17" data-mp="29xwn89e2yqdm" />}
                {current.page === 'my-home' && <MyHome onNavigate={navigate} onRequestTimeOff={openRequest} data-id="element-18" data-mp="2eu4yvct2k1g1" />}
                {current.page === 'my-timeoff' && <MyTimeOff tab={current.tab} onNavigate={navigate} onRequestTimeOff={openRequest} data-id="element-19" data-mp="2js6f7pz41mnt" />}
                {current.page === 'my-info' && <MyInfo data-id="element-20" data-mp="2supczvo1h937" />}
                {current.page === 'my-pay' && <MyPay data-id="element-21" data-mp="2su5xsmprwysv" />}
                {current.page === 'my-documents' && <MyDocuments data-id="element-22" data-mp="ti3ebch5g4f7" />}
              </PageErrorBoundary>
            </motion.div>
          </AnimatePresence>
        </main>
      </div>
      {view === 'employee' && <RequestTimeOffDialog open={requestOpen} onClose={() => setRequestOpen(false)} data-id="element-23" data-mp="yihpv4hylwhz" />}
      <Toaster position="bottom-right" toastOptions={{
      style: {
        fontFamily: 'Poppins, sans-serif',
        borderRadius: '14px'
      }
    }} data-id="element-24" data-mp="21toik2ur4x7t" />
    </div>;
}
const SECTION_ROUTES: Record<NavSection, Route> = {
  dashboard: {
    page: 'dashboard'
  },
  employees: {
    page: 'employees'
  },
  timeoff: {
    page: 'timeoff',
    tab: 'whos-out'
  },
  reports: {
    page: 'reports'
  },
  settings: {
    page: 'settings'
  },
  holidays: {
    page: 'holidays'
  },
  schedule: {
    page: 'schedule'
  },
  'my-home': {
    page: 'my-home'
  },
  'my-timeoff': {
    page: 'my-timeoff',
    tab: 'overview'
  },
  'my-info': {
    page: 'my-info'
  },
  'my-pay': {
    page: 'my-pay'
  },
  'my-documents': {
    page: 'my-documents'
  }
};
const SHARED_PAGES: Route['page'][] = ['holidays', 'schedule'];
const EMPLOYEE_PAGES: Route['page'][] = ['my-home', 'my-timeoff', 'my-info', 'my-pay', 'my-documents'];
function homeFor(view: AppView): Route {
  return view === 'admin' ? {
    page: 'dashboard'
  } : {
    page: 'my-home'
  };
}

/** Employees only ever reach their own pages; admins don't see the self-service pages. */
function isAllowed(route: Route, view: AppView): boolean {
  if (SHARED_PAGES.includes(route.page)) return true;
  const employeePage = EMPLOYEE_PAGES.includes(route.page);
  return view === 'employee' ? employeePage : !employeePage;
}
function sectionFor(route: Route): NavSection {
  switch (route.page) {
    case 'profile':
      return 'employees';
    case 'policy':
    case 'policy-edit':
      return 'timeoff';
    default:
      return route.page;
  }
}
function routeKey(route: Route): string {
  switch (route.page) {
    case 'profile':
      return `profile-${route.employeeId}`;
    case 'employees':
      return `employees-${route.segment ?? 'all'}`;
    case 'timeoff':
      return 'timeoff';
    case 'my-timeoff':
      return 'my-timeoff';
    case 'policy':
      return `policy-${route.policyId}`;
    case 'policy-edit':
      return `policy-edit-${route.policyId ?? 'new'}`;
    default:
      return route.page;
  }
}